if (!customElements.get('f-hero-slider')) {
  customElements.define('f-hero-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (this.slides.length < 2) return;
      this.abort = new AbortController();
      this.index = 0;
      delete this.dataset.transition;
      this.viewport = this.querySelector('[data-slides]');
      this.dots = [...this.querySelectorAll('[data-go]')];
      this.toggle = this.querySelector('[data-toggle]');
      this.status = this.querySelector('[data-status]');
      this.slides.forEach((slide, i) => { slide.setAttribute('aria-hidden', String(i !== 0)); slide.inert = i !== 0; });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === 0)));
      this.querySelector('[data-current]').textContent = '01';
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.editor = Boolean(window.Shopify?.designMode);
      this.stopped = this.dataset.autoplay !== 'true' || this.reduced.matches || this.editor;
      this.visible = !('IntersectionObserver' in window);
      this.hovered = false;
      this.focused = false;
      this.interval = Math.max(5000, Number(this.dataset.interval) || 8000);
      this.querySelector('[data-controls]').hidden = false;
      this.dataset.ready = '';
      const on = (node, event, handler) => node.addEventListener(event, handler, { signal: this.abort.signal });
      on(this.querySelector('[data-prev]'), 'click', () => this.go(this.index - 1));
      on(this.querySelector('[data-next]'), 'click', () => this.go(this.index + 1));
      this.dots.forEach((dot, i) => on(dot, 'click', () => this.go(i)));
      if (this.toggle) on(this.toggle, 'click', () => { this.stopped = !this.stopped; this.sync(); });
      on(this, 'keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || event.target.closest('input,textarea,select')) return;
        event.preventDefault();
        this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.sync(); } });
      on(this, 'pointerleave', event => { if (event.pointerType === 'mouse') { this.hovered = false; this.sync(); } });
      on(this, 'focusin', () => { this.focused = true; this.sync(); });
      on(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.sync(); } });
      on(document, 'visibilitychange', () => this.sync());
      on(this.reduced, 'change', () => { if (this.reduced.matches) this.stopped = true; this.sync(); });
      on(this.viewport, 'pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || event.target.closest('a,button') || (event.pointerType === 'mouse' && !event.target.closest('.f-hero-slider__media'))) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
        this.viewport.setPointerCapture(event.pointerId);
        this.sync();
      });
      on(this.viewport, 'pointerup', event => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        const dx = event.clientX - this.pointer.x, dy = event.clientY - this.pointer.y;
        this.pointer = null;
        if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(this.index + (dx < 0 ? 1 : -1));
        else this.sync();
      });
      on(this.viewport, 'pointercancel', () => { this.pointer = null; this.sync(); });
      on(this.viewport, 'lostpointercapture', () => { this.pointer = null; this.sync(); });
      on(this, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.go(index);
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.sync();
        }, { threshold: 0.25 });
        this.observer.observe(this);
      }
      this.sync();
    }
    go(target, manual = true) {
      const next = ((target % this.slides.length) + this.slides.length) % this.slides.length;
      if (manual) this.stopped = true;
      if (next !== this.index) {
        this.dataset.transition = '';
        if (this.slides[this.index].contains(document.activeElement)) this.dots[next].focus({ preventScroll: true });
        this.style.setProperty('--slide-shift', target < this.index ? '-16px' : '16px');
        this.index = next;
        this.slides.forEach((slide, i) => { slide.setAttribute('aria-hidden', String(i !== next)); slide.inert = i !== next; });
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === next)));
        this.querySelector('[data-current]').textContent = String(next + 1).padStart(2, '0');
        const image = this.slides[next].querySelector('img');
        if (image) { image.loading = 'eager'; image.fetchPriority = 'auto'; }
        if (manual) this.status.textContent = this.slides[next].getAttribute('aria-label');
      }
      this.sync();
    }
    sync() {
      clearTimeout(this.timer);
      if (this.toggle) {
        this.toggle.hidden = this.reduced.matches || this.editor;
        this.toggle.dataset.paused = String(this.stopped);
        this.toggle.setAttribute('aria-label', this.stopped ? this.toggle.dataset.play : this.toggle.dataset.pause);
      }
      if (!this.stopped && !this.reduced.matches && !this.editor && this.visible && !document.hidden && !this.hovered && !this.focused && !this.pointer) this.timer = setTimeout(() => this.go(this.index + 1, false), this.interval);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.observer?.disconnect();
      this.abort = null;
    }
  });
}
